import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from './api';
import { Icons } from './Icons';

const STATUS_MAP = {
  available: 'Verfügbar', defect: 'Defekt', repair: 'In Reparatur',
  lent: 'Verliehen', for_sale: 'Zu verkaufen', sold: 'Verkauft',
  to_dispose: 'Zu entsorgen', disposed: 'Entsorgt'
};
const ROLE_LABELS = { admin: 'Admin', editor: 'Bearbeiter', readonly: 'Nur-Lesen' };

// === Item Form (Create / Edit) ===
export function ItemFormPage({ auth, toast: addToast }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [form, setForm] = useState({
    name: '', category_id: '', company_id: '', status: 'available', lent_to: '',
    barcode: '', serial_number: '', purchase_date: '', purchase_price: '', is_asset: '', asset_company_id: '', note_accounting: '',
    current_room_id: '', target_room_id: '', dimensions: '', comment: ''
  });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [barcodeScanning, setBarcodeScanning] = useState(false);
  const [barcodeCandidate, setBarcodeCandidate] = useState(null);
  const barcodeVideoRef = useRef(null);
  const barcodeStreamRef = useRef(null);
  const barcodeAnimRef = useRef(null);
  const barcodeScanActiveRef = useRef(false);

  useEffect(() => {
    api.get('/companies').then(r => setCompanies(r.data));
    api.get('/categories').then(r => setCategories(r.data));
    api.get('/rooms').then(r => setRooms(r.data));
    if (isEdit) {
      api.get(`/items/${id}`).then(r => {
        const d = r.data;
        setForm({
          name: d.name, category_id: d.category_id || '', company_id: d.company_id || '',
          status: d.status, lent_to: d.lent_to || '', barcode: d.barcode || '',
          serial_number: d.serial_number || '',
          purchase_date: d.purchase_date || '',
          purchase_price: d.purchase_price || '', is_asset: d.is_asset === 1 ? '1' : d.is_asset === 0 ? '0' : '',
          asset_company_id: d.asset_company_id || '', note_accounting: d.note_accounting || '',
          current_room_id: d.current_room_id || '', target_room_id: d.target_room_id || '',
          dimensions: d.dimensions || '', comment: d.comment || ''
        });
        if (d.photo) setPhotoPreview(`/api/uploads/${d.photo}`);
      });
    }
    return () => {
      barcodeScanActiveRef.current = false;
      if (barcodeStreamRef.current) { barcodeStreamRef.current.getTracks().forEach(t => t.stop()); }
    };
  }, [id, isEdit]);

  const stopBarcodeScanner = useCallback(() => {
    barcodeScanActiveRef.current = false;
    if (barcodeAnimRef.current) { cancelAnimationFrame(barcodeAnimRef.current); barcodeAnimRef.current = null; }
    if (barcodeVideoRef.current) { barcodeVideoRef.current.srcObject = null; }
    if (barcodeStreamRef.current) { barcodeStreamRef.current.getTracks().forEach(t => t.stop()); barcodeStreamRef.current = null; }
    setBarcodeScanning(false);
  }, []);

  const confirmBarcode = (code) => {
    setBarcodeCandidate(code);
    stopBarcodeScanner();
  };

  const acceptBarcode = () => {
    if (barcodeCandidate) {
      setForm(f => ({ ...f, barcode: barcodeCandidate }));
      if (addToast) addToast('Barcode übernommen: ' + barcodeCandidate);
    }
    setBarcodeCandidate(null);
  };

  const rejectBarcode = () => {
    setBarcodeCandidate(null);
  };

  // Crop center region of video for scanning to avoid picking up background barcodes
  const cropCenterCanvas = useRef(null);
  const getCenterCrop = (video) => {
    if (!cropCenterCanvas.current) cropCenterCanvas.current = document.createElement('canvas');
    const canvas = cropCenterCanvas.current;
    const cropW = Math.floor(video.videoWidth * 0.5);
    const cropH = Math.floor(video.videoHeight * 0.4);
    const sx = Math.floor((video.videoWidth - cropW) / 2);
    const sy = Math.floor((video.videoHeight - cropH) / 2);
    canvas.width = cropW;
    canvas.height = cropH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, sx, sy, cropW, cropH, 0, 0, cropW, cropH);
    return canvas;
  };

  const startBarcodeScanner = async () => {
    stopBarcodeScanner();
    setError('');
    setBarcodeCandidate(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      barcodeStreamRef.current = stream;
      barcodeScanActiveRef.current = true;
      setBarcodeScanning(true);
      await new Promise(r => setTimeout(r, 50));
      if (!barcodeScanActiveRef.current || !barcodeVideoRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
      const video = barcodeVideoRef.current;
      video.srcObject = stream;
      video.setAttribute('playsinline', 'true');
      video.muted = true;
      await video.play();
      if (!barcodeScanActiveRef.current) return;

      if ('BarcodeDetector' in window) {
        const detector = new window.BarcodeDetector({ formats: ['code_128', 'code_39', 'ean_13', 'ean_8'] });
        const scan = () => {
          if (!barcodeScanActiveRef.current || !barcodeVideoRef.current) return;
          if (barcodeVideoRef.current.readyState < 2) { barcodeAnimRef.current = requestAnimationFrame(scan); return; }
          const cropped = getCenterCrop(barcodeVideoRef.current);
          detector.detect(cropped).then(barcodes => {
            if (!barcodeScanActiveRef.current) return;
            if (barcodes.length > 0) {
              confirmBarcode(barcodes[0].rawValue);
              return;
            }
            barcodeAnimRef.current = requestAnimationFrame(scan);
          }).catch(() => {
            if (!barcodeScanActiveRef.current) return;
            setTimeout(() => { if (barcodeScanActiveRef.current) barcodeAnimRef.current = requestAnimationFrame(scan); }, 200);
          });
        };
        barcodeAnimRef.current = requestAnimationFrame(scan);
      } else {
        try {
          const { BrowserMultiFormatReader } = await import('@zxing/library');
          if (!barcodeScanActiveRef.current) return;
          const reader = new BrowserMultiFormatReader();
          reader.decodeFromVideoDevice(null, video, (res) => {
            if (res && barcodeScanActiveRef.current) {
              confirmBarcode(res.getText());
              reader.reset();
            }
          });
        } catch { setError('Scanner nicht unterstützt.'); stopBarcodeScanner(); }
      }
    } catch { setError('Kamera-Zugriff verweigert.'); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const fd = new FormData();
      // Explicit FormData assembly – booleans and empty values must be handled carefully
      fd.append('name', form.name);
      if (form.company_id) fd.append('company_id', form.company_id);
      fd.append('status', form.status || 'available');
      fd.append('is_asset', form.is_asset);
      if (form.category_id) fd.append('category_id', form.category_id);
      if (form.lent_to) fd.append('lent_to', form.lent_to);
      if (form.barcode) fd.append('barcode', form.barcode);
      if (form.serial_number) fd.append('serial_number', form.serial_number);
      if (form.purchase_date) fd.append('purchase_date', form.purchase_date);
      if (form.purchase_price !== '' && form.purchase_price !== null && form.purchase_price !== undefined) fd.append('purchase_price', form.purchase_price);
      if (form.is_asset === '1' && form.asset_company_id) fd.append('asset_company_id', form.asset_company_id);
      if (form.note_accounting) fd.append('note_accounting', form.note_accounting);
      if (form.current_room_id) fd.append('current_room_id', form.current_room_id);
      if (form.target_room_id) fd.append('target_room_id', form.target_room_id);
      if (form.dimensions) fd.append('dimensions', form.dimensions);
      if (form.comment) fd.append('comment', form.comment);
      if (photo) fd.append('photo', photo);

      if (isEdit) {
        await api.put(`/items/${id}`, fd);
        addToast('Item aktualisiert');
        navigate(`/items/${id}`);
      } else {
        const res = await api.post('/items', fd);
        addToast('Item erstellt');
        navigate(`/items/${res.data.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(isEdit ? `/items/${id}` : '/items')}><Icons.ChevronLeft /> Zurück</button>
          <h1 className="page-title">{isEdit ? 'Item bearbeiten' : 'Neues Item'}</h1>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">Name</label>
                <input className="form-input" name="name" value={form.name} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label className="form-label">Firma</label>
                <select className="form-select" name="company_id" value={form.company_id} onChange={handleChange}>
                  <option value="">Unbekannt</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">Kategorie</label>
                <select className="form-select" name="category_id" value={form.category_id} onChange={handleChange} required>
                  <option value="" disabled>Bitte auswählen</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" name="status" value={form.status} onChange={handleChange}>
                  {Object.entries(STATUS_MAP).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>

            {form.status === 'lent' && (
              <div className="form-group">
                <label className="form-label">Verliehen an</label>
                <input className="form-input" name="lent_to" value={form.lent_to} onChange={handleChange} placeholder="Name der Person / Firma" />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Barcode (Etiketten-Nummer)</label>
              <div style={{display:'flex',gap:8}}>
                <input className="form-input" style={{flex:1}} name="barcode" value={form.barcode} onChange={handleChange} placeholder="z.B. 00042" />
                <button type="button" className={`btn ${barcodeScanning ? 'btn-danger' : 'btn-secondary'}`} onClick={barcodeScanning ? stopBarcodeScanner : startBarcodeScanner}>
                  <Icons.Scan /> {barcodeScanning ? 'Stopp' : 'Scannen'}
                </button>
              </div>
              {barcodeScanning && (
                <div style={{marginTop:8,position:'relative'}}>
                  <video ref={barcodeVideoRef} style={{width:'100%',maxWidth:400,borderRadius:'var(--radius)'}} playsInline muted />
                  <div style={{position:'absolute',top:'30%',left:'25%',right:'25%',bottom:'30%',border:'2px solid var(--accent)',borderRadius:4,pointerEvents:'none',opacity:0.7}} />
                </div>
              )}
              {barcodeCandidate && (
                <div style={{marginTop:8,padding:'12px 16px',background:'var(--accent-light)',border:'1px solid var(--accent)',borderRadius:'var(--radius)',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                  <span>Erkannt: <strong style={{fontFamily:'var(--font-mono)',fontSize:'1.1rem'}}>{barcodeCandidate}</strong></span>
                  <div className="btn-group">
                    <button type="button" className="btn btn-primary btn-sm" onClick={acceptBarcode}>Übernehmen</button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={rejectBarcode}>Verwerfen</button>
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Serien-/Gerätenummer</label>
              <input
                className="form-input"
                name="serial_number"
                value={form.serial_number}
                onChange={handleChange}
                placeholder="Falls vorhanden bitte eintragen – hilft der Buchhaltung beim Verkauf etc."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">Aktueller Raum</label>
                <select className="form-select" name="current_room_id" value={form.current_room_id} onChange={handleChange} required>
                  <option value="" disabled>Bitte auswählen</option>
                  {rooms
                    .filter(r => r.type === 'alt')
                    .sort((a, b) => {
                      // Lager Langenfeld oben, dann alphabetisch
                      if (a.name === 'Lager Langenfeld') return -1;
                      if (b.name === 'Lager Langenfeld') return 1;
                      return a.name.localeCompare(b.name, 'de');
                    })
                    .map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Zielraum</label>
                <select className="form-select" name="target_room_id" value={form.target_room_id} onChange={handleChange}>
                  <option value="">Kein Zielraum</option>
                  {rooms.filter(r => r.type === 'neu').map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Abmessungen</label>
              <input className="form-input" name="dimensions" value={form.dimensions} onChange={handleChange} placeholder="L × B × H in cm, z.B. 120×60×75" />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Kaufdatum</label>
                <input className="form-input" type="date" name="purchase_date" value={form.purchase_date} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Kaufpreis (€)</label>
                <input className="form-input" type="number" step="0.01" name="purchase_price" value={form.purchase_price} onChange={handleChange} placeholder="0.00" />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Anlagevermögen</label>
              <select className="form-select" name="is_asset" value={form.is_asset} onChange={handleChange}>
                <option value="">Nicht geprüft</option>
                <option value="1">Ja</option>
                <option value="0">Nein</option>
              </select>
            </div>

            {form.is_asset === '1' && (
              <div className="form-group">
                <label className="form-label">Anlagefirma</label>
                <select className="form-select" name="asset_company_id" value={form.asset_company_id} onChange={handleChange}>
                  <option value="">Bitte wählen…</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Notiz Buchhaltung</label>
              <textarea className="form-textarea" name="note_accounting" value={form.note_accounting} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label className="form-label">Kommentar</label>
              <textarea className="form-textarea" name="comment" value={form.comment} onChange={handleChange} placeholder="z.B. Bein wackelt, muss zerlegt werden, zuerst rausbringen" />
            </div>

            <div className="form-group">
              <label className="form-label">Foto</label>
              <div className="photo-upload" onClick={() => document.getElementById('photo-input').click()}>
                <Icons.Camera style={{width:32,height:32,color:'var(--text-muted)',marginBottom:8}} />
                <p style={{color:'var(--text-muted)',fontSize:'0.85rem'}}>Klicken zum Hochladen (max. 10 MB)</p>
                <input id="photo-input" type="file" accept="image/*" onChange={handlePhoto} style={{display:'none'}} />
              </div>
              {photoPreview && <img src={photoPreview} alt="Vorschau" className="photo-preview" />}
            </div>

            <div className="btn-group" style={{marginTop:24}}>
              <button className="btn btn-primary btn-lg" type="submit" disabled={saving}>
                {saving ? 'Wird gespeichert…' : (isEdit ? 'Aktualisieren' : 'Erstellen')}
              </button>
              <button className="btn btn-secondary btn-lg" type="button" onClick={() => navigate(isEdit ? `/items/${id}` : '/items')}>Abbrechen</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// === Scanner Page ===
export function ScannerPage({ toast: addToast }) {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const streamRef = useRef(null);
  const animRef = useRef(null);
  const readerRef = useRef(null);
  const [mode, setMode] = useState('search'); // 'search' or 'link'
  const [unlinkedItems, setUnlinkedItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [linkSuccess, setLinkSuccess] = useState('');

  // Load items without barcode for link mode
  useEffect(() => {
    if (mode === 'link') {
      api.get('/items/unlinked/list').then(r => setUnlinkedItems(r.data));
    }
  }, [mode]);

  // Ref to track if scan-loop is active (prevents multiple loops)
  const scanActiveRef = useRef(false);

  const stopScanner = useCallback(() => {
    scanActiveRef.current = false;
    if (animRef.current) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }
    if (readerRef.current) {
      try { readerRef.current.reset(); } catch {}
      readerRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.onplaying = null;
      videoRef.current.srcObject = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setScanning(false);
  }, []);

  // Use ref for handleBarcodeScan to avoid stale closures in the scan loop
  const handleBarcodeScanRef = useRef(null);
  handleBarcodeScanRef.current = async (code) => {
    stopScanner();
    if (mode === 'search') {
      setError(''); setResult(null); setLinkSuccess('');
      try {
        const res = await api.get(`/items/barcode/${code}`);
        setResult(res.data);
      } catch {
        setError(`Kein Item mit Barcode "${code}" gefunden`);
      }
    } else {
      setManualCode(code);
      if (selectedItemId) {
        setError(''); setLinkSuccess('');
        try {
          const res = await api.post(`/items/${selectedItemId}/link-barcode`, { barcode: code.trim() });
          setLinkSuccess(`Barcode "${code.trim()}" wurde mit "${res.data.name}" verknüpft!`);
          setResult(res.data);
          setManualCode('');
          setSelectedItemId('');
          api.get('/items/unlinked/list').then(r => setUnlinkedItems(r.data));
          if (addToast) addToast(`Barcode ${code.trim()} verknüpft`);
        } catch (err) {
          setError(err.response?.data?.error || 'Fehler beim Verknüpfen');
        }
      }
    }
  };

  const startScanner = async () => {
    // Stop any previous scanner first
    stopScanner();
    setError(''); setResult(null); setLinkSuccess('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      scanActiveRef.current = true;
      setScanning(true);

      // Wait for React to render the video element
      await new Promise(r => setTimeout(r, 50));

      // Verify we're still active (user might have pressed stop in the meantime)
      if (!scanActiveRef.current || !videoRef.current || !streamRef.current) {
        stream.getTracks().forEach(t => t.stop());
        return;
      }

      const video = videoRef.current;
      video.srcObject = stream;
      video.setAttribute('playsinline', 'true');
      video.muted = true;

      // Wait for video to actually be ready
      await video.play();

      // Double-check still active after await
      if (!scanActiveRef.current) return;

      if ('BarcodeDetector' in window) {
        const detector = new window.BarcodeDetector({
          formats: ['code_128', 'code_39', 'ean_13', 'ean_8', 'qr_code', 'codabar', 'itf']
        });
        const scan = () => {
          if (!scanActiveRef.current || !videoRef.current) return;
          // Only detect when video has actual frame data
          if (videoRef.current.readyState < 2) {
            animRef.current = requestAnimationFrame(scan);
            return;
          }
          detector.detect(videoRef.current).then(barcodes => {
            if (!scanActiveRef.current) return;
            if (barcodes.length > 0) {
              handleBarcodeScanRef.current(barcodes[0].rawValue);
              return;
            }
            animRef.current = requestAnimationFrame(scan);
          }).catch(() => {
            if (!scanActiveRef.current) return;
            // Small delay on error to avoid tight error loop
            setTimeout(() => {
              if (scanActiveRef.current) {
                animRef.current = requestAnimationFrame(scan);
              }
            }, 200);
          });
        };
        animRef.current = requestAnimationFrame(scan);
      } else {
        // Fallback: @zxing/library
        try {
          const { BrowserMultiFormatReader } = await import('@zxing/library');
          if (!scanActiveRef.current) return;
          const reader = new BrowserMultiFormatReader();
          readerRef.current = reader;
          reader.decodeFromVideoDevice(null, video, (res) => {
            if (res && scanActiveRef.current) {
              handleBarcodeScanRef.current(res.getText());
            }
          });
        } catch {
          setError('Barcode-Scanner wird auf diesem Gerät nicht unterstützt. Bitte Code manuell eingeben.');
          stopScanner();
        }
      }
    } catch {
      setError('Kamera-Zugriff verweigert. Bitte Berechtigung erteilen.');
    }
  };

  useEffect(() => () => stopScanner(), [stopScanner]);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    if (mode === 'search') {
      setError(''); setResult(null); setLinkSuccess('');
      api.get(`/items/barcode/${manualCode.trim()}`).then(r => setResult(r.data))
        .catch(() => setError(`Kein Item mit Barcode "${manualCode.trim()}" gefunden`));
    } else {
      if (!selectedItemId) { setError('Bitte zuerst ein Item auswählen'); return; }
      setError(''); setLinkSuccess('');
      api.post(`/items/${selectedItemId}/link-barcode`, { barcode: manualCode.trim() })
        .then(r => {
          setLinkSuccess(`Barcode "${manualCode.trim()}" wurde mit "${r.data.name}" verknüpft!`);
          setResult(r.data);
          setManualCode('');
          setSelectedItemId('');
          api.get('/items/unlinked/list').then(res => setUnlinkedItems(res.data));
          if (addToast) addToast(`Barcode ${manualCode.trim()} verknüpft`);
        })
        .catch(err => setError(err.response?.data?.error || 'Fehler beim Verknüpfen'));
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div><h1 className="page-title">Barcode-Scanner</h1><p className="page-subtitle">{mode === 'search' ? 'Item per Barcode finden' : 'Barcode mit Item verknüpfen'}</p></div>
      </div>

      {/* Mode Toggle */}
      <div className="tabs" style={{marginBottom: 24}}>
        <button className={`tab ${mode === 'search' ? 'active' : ''}`} onClick={() => { setMode('search'); setResult(null); setError(''); setLinkSuccess(''); }}>
          Item suchen
        </button>
        <button className={`tab ${mode === 'link' ? 'active' : ''}`} onClick={() => { setMode('link'); setResult(null); setError(''); setLinkSuccess(''); }}>
          Barcode verknüpfen
        </button>
      </div>

      {/* Link mode: select item first */}
      {mode === 'link' && (
        <div className="card" style={{marginBottom: 20}}>
          <div className="card-header"><h3 className="card-title">1. Item auswählen (ohne Barcode)</h3></div>
          <div className="card-body">
            {unlinkedItems.length === 0 ? (
              <p style={{color:'var(--text-muted)'}}>Alle Items haben bereits einen Barcode.</p>
            ) : (
              <select className="form-select" value={selectedItemId} onChange={e => setSelectedItemId(e.target.value)}>
                <option value="">Bitte Item wählen…</option>
                {unlinkedItems.map(i => (
                  <option key={i.id} value={i.id}>{i.name} ({i.company_name})</option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      <div className="scanner-grid">
        <div className="card">
          <div className="card-header"><h3 className="card-title">{mode === 'link' ? '2. Barcode scannen' : 'Kamera-Scanner'}</h3></div>
          <div className="card-body" style={{textAlign:'center'}}>
            {scanning ? (
              <div className="scanner-container">
                <video ref={videoRef} style={{width:'100%',borderRadius:'var(--radius)'}} playsInline muted />
                <div className="scanner-overlay"><div className="scanner-crosshair" /></div>
                <button className="btn btn-danger" style={{marginTop:12}} onClick={stopScanner}><Icons.X /> Stopp</button>
              </div>
            ) : (
              <button className="btn btn-primary btn-lg" onClick={startScanner}
                disabled={mode === 'link' && !selectedItemId}>
                <Icons.Camera /> Scanner starten
              </button>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3 className="card-title">{mode === 'link' ? '…oder Nummer eintippen' : 'Manuelle Eingabe'}</h3></div>
          <div className="card-body">
            <form onSubmit={handleManualSubmit}>
              <div className="form-group">
                <label className="form-label">Barcode-Nummer</label>
                <input className="form-input" value={manualCode} onChange={e => setManualCode(e.target.value)} placeholder="z.B. 00042" />
              </div>
              <button className="btn btn-primary" type="submit"
                disabled={mode === 'link' && !selectedItemId}>
                {mode === 'search' ? 'Suchen' : 'Verknüpfen'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{marginTop:20}}>{error}</div>}
      {linkSuccess && <div className="alert alert-success" style={{marginTop:20}}>{linkSuccess}</div>}

      {result && (
        <div className="card" style={{marginTop:20}}>
          <div className="card-header">
            <h3 className="card-title">{mode === 'link' ? 'Verknüpft' : 'Gefunden'}: {result.name}</h3>
            <button className="btn btn-primary btn-sm" onClick={() => navigate(`/items/${result.id}`)}>
              <Icons.Eye /> Details anzeigen
            </button>
          </div>
          <div className="card-body">
            <div className="detail-grid">
              <div>
                <div className="detail-field"><div className="detail-label">Barcode</div><div className="detail-value barcode-text">{result.barcode || '(keiner)'}</div></div>
                <div className="detail-field"><div className="detail-label">Firma</div><div className="detail-value">{result.company_name}</div></div>
                <div className="detail-field"><div className="detail-label">Status</div><div className="detail-value"><span className={`badge badge-${result.status}`}>{STATUS_MAP[result.status]}</span></div></div>
              </div>
              <div>
                <div className="detail-field"><div className="detail-label">Kategorie</div><div className="detail-value">{result.category_name || '–'}</div></div>
                {result.status === 'lent' && <div className="detail-field"><div className="detail-label">Verliehen an</div><div className="detail-value">{result.lent_to}</div></div>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// === Users Page ===
export function UsersPage({ toast: addToast }) {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', role: 'editor' });
  const [error, setError] = useState('');

  const fetchUsers = () => api.get('/auth/users').then(r => setUsers(r.data));
  useEffect(() => { fetchUsers(); }, []);

  const openNew = () => { setEditUser(null); setForm({ name: '', email: '', role: 'editor' }); setError(''); setShowModal(true); };
  const openEdit = (u) => { setEditUser(u); setForm({ name: u.name, email: u.email, role: u.role }); setError(''); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editUser) {
        await api.put(`/auth/users/${editUser.id}`, { name: form.name, email: form.email, role: form.role, active: true });
        addToast('Benutzer aktualisiert');
      } else {
        await api.post('/auth/users', form);
        addToast('Benutzer erstellt');
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) { setError(err.response?.data?.error || 'Fehler'); }
  };

  const handleToggleActive = async (u) => {
    try {
      await api.put(`/auth/users/${u.id}`, { active: !u.active });
      addToast(u.active ? 'Benutzer deaktiviert' : 'Benutzer aktiviert');
      fetchUsers();
    } catch { addToast('Fehler', 'error'); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Benutzer "${u.name}" (${u.email}) endgültig löschen?`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      addToast('Benutzer gelöscht');
      fetchUsers();
    } catch (err) { addToast(err.response?.data?.error || 'Fehler', 'error'); }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div><h1 className="page-title">Benutzerverwaltung</h1><p className="page-subtitle">{users.length} Benutzer</p></div>
        <button className="btn btn-primary" onClick={openNew}><Icons.Plus /> Neuer Benutzer</button>
      </div>

      <div className="table-container">
        <table className="table">
          <thead><tr><th>Name</th><th>E-Mail</th><th>Rolle</th><th>Status</th><th>Erstellt</th><th></th></tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td style={{fontWeight:500}}>{u.name}</td>
                <td>{u.email}</td>
                <td><span className={`badge badge-${u.role}`}>{ROLE_LABELS[u.role]}</span></td>
                <td><span className={`badge ${u.active ? 'badge-available' : 'badge-defect'}`}>{u.active ? 'Aktiv' : 'Inaktiv'}</span></td>
                <td style={{fontSize:'0.8rem',color:'var(--text-muted)'}}>{new Date(u.created_at).toLocaleDateString('de-DE')}</td>
                <td>
                  <div className="btn-group">
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(u)}><Icons.Edit /></button>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleToggleActive(u)}>{u.active ? 'Deaktivieren' : 'Aktivieren'}</button>
                    <button className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}} onClick={() => handleDelete(u)}><Icons.Trash /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editUser ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><Icons.X style={{width:20,height:20}} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-error">{error}</div>}
                <div className="form-group">
                  <label className="form-label required">Name</label>
                  <input className="form-input" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required />
                </div>
                <div className="form-group">
                  <label className="form-label required">E-Mail</label>
                  <input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} required placeholder="name@chp-gruppe.com" />
                  <div style={{fontSize:'0.8rem',color:'var(--text-muted)',marginTop:6}}>Der Nutzer meldet sich mit seinem Microsoft-365-Account an. Die E-Mail muss mit dem M365-Konto übereinstimmen.</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Rolle</label>
                  <select className="form-select" value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))}>
                    <option value="admin">Admin</option>
                    <option value="editor">Bearbeiter</option>
                    <option value="readonly">Nur-Lesen</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" type="button" onClick={() => setShowModal(false)}>Abbrechen</button>
                <button className="btn btn-primary" type="submit">{editUser ? 'Aktualisieren' : 'Erstellen'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// === Categories Page ===
export function CategoriesPage({ toast: addToast }) {
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const fetchCats = () => api.get('/categories').then(r => setCategories(r.data));
  useEffect(() => { fetchCats(); }, []);

  const openNew = () => { setEditCat(null); setName(''); setError(''); setShowModal(true); };
  const openEdit = (c) => { setEditCat(c); setName(c.name); setError(''); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editCat) {
        await api.put(`/categories/${editCat.id}`, { name });
        addToast('Kategorie aktualisiert');
      } else {
        await api.post('/categories', { name });
        addToast('Kategorie erstellt');
      }
      setShowModal(false);
      fetchCats();
    } catch (err) { setError(err.response?.data?.error || 'Fehler'); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Kategorie "${c.name}" wirklich löschen?`)) return;
    try { await api.delete(`/categories/${c.id}`); addToast('Kategorie gelöscht'); fetchCats(); }
    catch (err) { addToast(err.response?.data?.error || 'Fehler beim Löschen', 'error'); }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div><h1 className="page-title">Kategorien</h1><p className="page-subtitle">{categories.length} Kategorien</p></div>
        <button className="btn btn-primary" onClick={openNew}><Icons.Plus /> Neue Kategorie</button>
      </div>

      <div className="table-container">
        <table className="table">
          <thead><tr><th>Name</th><th>Erstellt</th><th></th></tr></thead>
          <tbody>
            {categories.map(c => (
              <tr key={c.id}>
                <td style={{fontWeight:500}}>{c.name}</td>
                <td style={{fontSize:'0.8rem',color:'var(--text-muted)'}}>{new Date(c.created_at).toLocaleDateString('de-DE')}</td>
                <td>
                  <div className="btn-group">
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(c)}><Icons.Edit /></button>
                    <button className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}} onClick={() => handleDelete(c)}><Icons.Trash /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{maxWidth:440}} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editCat ? 'Kategorie bearbeiten' : 'Neue Kategorie'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><Icons.X style={{width:20,height:20}} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert alert-error">{error}</div>}
                <div className="form-group">
                  <label className="form-label required">Name</label>
                  <input className="form-input" value={name} onChange={e => setName(e.target.value)} required autoFocus />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" type="button" onClick={() => setShowModal(false)}>Abbrechen</button>
                <button className="btn btn-primary" type="submit">{editCat ? 'Aktualisieren' : 'Erstellen'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

